import {
  KoButton,
  KoLoader,
  KoMonaco,
  KoSelect,
  KoTable,
  // KoText,
  // KoTitle,
  // KoTag,
  KoToast,
  KoEmpty
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { ApiPlatformAPI } from '../services/apiplatform.service';
import { COLUMNS_CONTRACT_DIFF } from '../constants';
import ApiSubNav from './ApiSubNav';
import styles from './apiplatform.module.scss';

const MOCK_SERVICE_LIST = [
  { label: 'User Service', value: 'svc-001', versions: ['v3.2.1', 'v3.1.0', 'v3.0.0'] },
  { label: 'Payment Gateway', value: 'svc-002', versions: ['v4.1.0', 'v4.0.0', 'v3.9.0'] },
  { label: 'Notification Hub', value: 'svc-003', versions: ['v1.5.3', 'v1.5.0', 'v1.4.0'] }
];

const MOCK_DIFF_RESULTS = [
  { path: '/users/{id}', changeType: 'modified', before: 'GET — Returns UserV2', after: 'GET — Returns UserV3', breaking: true },
  { path: '/users/{id}/avatar', changeType: 'added', before: null, after: 'POST — Upload avatar image', breaking: false },
  { path: '/users/search', changeType: 'deprecated', before: 'POST — Full-text search', after: 'POST — Deprecated, use /search/query', breaking: false },
  { path: '/payments/charge', changeType: 'modified', before: 'amount: integer', after: 'amount: string (decimal)', breaking: true },
  { path: '/payments/subscription/pause', changeType: 'added', before: null, after: 'POST — Pause active subscription', breaking: false },
  { path: '/billing/invoices', changeType: 'removed', before: 'GET — List invoices', after: null, breaking: true },
  { path: '/notifications/sms', changeType: 'added', before: null, after: 'POST — Send SMS notification', breaking: false },
  { path: '/auth/mfa', changeType: 'modified', before: 'totp_code: required', after: 'totp_code: optional (when backup code provided)', breaking: false }
];

function ContractDiff() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [isLoading, setLoading] = useState(false);
  const [serviceList, setServiceList] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [versionA, setVersionA] = useState(null);
  const [versionB, setVersionB] = useState(null);
  const [versionOptions, setVersionOptions] = useState([]);

  const [diffResults, setDiffResults] = useState([]);
  const [schemaA, setSchemaA] = useState('');
  const [schemaB, setSchemaB] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'sideBySide'

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/apiplatform`
    : '/apiplatform';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'API Platform', path: basePath },
      { title: 'Contract Diff' }
    ]));
    fetchServiceList();
  }, [workspaceId]);

  useEffect(() => {
    if (selectedService) {
      const mockSvc = MOCK_SERVICE_LIST.find(s => s.value === selectedService);
      const mockVersions = mockSvc ? mockSvc.versions.map(v => ({ label: v, value: v })) : [];
      ApiPlatformAPI.getServiceById(selectedService).then(
        (data) => {
          const versions = (data?.versions || []).map(v => ({ label: v, value: v }));
          setVersionOptions(versions.length > 0 ? versions : mockVersions);
          setVersionA(null);
          setVersionB(null);
          setDiffResults([]);
          setSchemaA('');
          setSchemaB('');
        },
        () => {
          setVersionOptions(mockVersions);
          setVersionA(null);
          setVersionB(null);
          setDiffResults([]);
          setSchemaA('');
          setSchemaB('');
        }
      );
    }
  }, [selectedService]);

  const fetchServiceList = () => {
    ApiPlatformAPI.getServiceList().then(
      (data) => {
        const list = (data?.data || data || []).map(s => ({ label: s.name, value: s._id || s.serviceId }));
        setServiceList(list.length > 0 ? list : MOCK_SERVICE_LIST);
      },
      () => setServiceList(MOCK_SERVICE_LIST)
    );
  };

  const handleCompare = () => {
    if (!selectedService || !versionA || !versionB) {
      KoToast.error('Please select a service and two versions to compare.');
      return;
    }
    setLoading(true);
    ApiPlatformAPI.compareVersions(selectedService, versionA, versionB).then(
      (res) => {
        const changes = res?.changes || res?.data || res || [];
        setDiffResults(changes.length > 0 ? changes : MOCK_DIFF_RESULTS);
        setSchemaA(typeof res?.schemaA === 'string' ? res.schemaA : JSON.stringify(res?.schemaA || {}, null, 2));
        setSchemaB(typeof res?.schemaB === 'string' ? res.schemaB : JSON.stringify(res?.schemaB || {}, null, 2));
        setLoading(false);
      },
      () => {
        setDiffResults(MOCK_DIFF_RESULTS);
        setLoading(false);
      }
    );
  };

  const summary = {
    added: diffResults.filter(d => d.changeType === 'added').length,
    modified: diffResults.filter(d => d.changeType === 'modified').length,
    removed: diffResults.filter(d => d.changeType === 'removed').length,
    breaking: diffResults.filter(d => d.breaking).length
  };

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <ApiSubNav activeKey='contract-diff' />

      <KoToolBar
        title='Contract Diff'
        search={{ visible: false }}
        filter={{ visible: false, options: [] }}
        buttonList={[]}
      />

      {/* Selection Bar */}
      <div className={styles.diffSelectionBar}>
        <KoSelect
          style={{ minWidth: 200 }}
          value={selectedService}
          onChange={setSelectedService}
          placeholder='Select service'
          options={serviceList}
        />
        <KoSelect
          style={{ minWidth: 140 }}
          value={versionA}
          onChange={setVersionA}
          placeholder='Version A'
          options={versionOptions}
        />
        <KoIcon name='SwapOutlined' width={16} height={16} />
        <KoSelect
          style={{ minWidth: 140 }}
          value={versionB}
          onChange={setVersionB}
          placeholder='Version B'
          options={versionOptions}
        />
        <KoButton
          type='primary'
          label='Compare'
          onClick={handleCompare}
          icon={<KoIcon name='DiffOutlined' width={14} height={14} color='#FFF' />}
        />
        {diffResults.length > 0 && (
          <KoButton
            label={viewMode === 'table' ? 'Side-by-Side' : 'Table View'}
            onClick={() => setViewMode(viewMode === 'table' ? 'sideBySide' : 'table')}
            icon={<KoIcon name={viewMode === 'table' ? 'ColumnWidthOutlined' : 'TableOutlined'} width={14} height={14} />}
          />
        )}
      </div>

      {diffResults.length === 0 && !isLoading && (
        <KoEmpty description='Select a service and two versions to compare contracts' />
      )}

      {diffResults.length > 0 && (
        <>
          {/* Summary Strip */}
          <div className={styles.diffSummaryStrip}>
            <div className={`${styles.diffSummaryItem} ${styles.diffAdded}`}>
              <KoIcon name='PlusCircleOutlined' width={14} height={14} />
              {summary.added} Added
            </div>
            <div className={`${styles.diffSummaryItem} ${styles.diffModified}`}>
              <KoIcon name='EditOutlined' width={14} height={14} />
              {summary.modified} Modified
            </div>
            <div className={`${styles.diffSummaryItem} ${styles.diffRemoved}`}>
              <KoIcon name='MinusCircleOutlined' width={14} height={14} />
              {summary.removed} Removed
            </div>
            <div className={`${styles.diffSummaryItem} ${styles.diffBreaking}`}>
              <KoIcon name='WarningOutlined' width={14} height={14} />
              {summary.breaking} Breaking
            </div>
          </div>

          {viewMode === 'table' && (
            <div style={{ padding: '0 1.6rem 1.6rem' }}>
              <KoTable
                maxHeight={440}
                dataSource={diffResults}
                columns={COLUMNS_CONTRACT_DIFF}
                rowKey={(r) => r.path || r._id}
              />
            </div>
          )}

          {viewMode === 'sideBySide' && (
            <div className={styles.diffView}>
              <div className={styles.diffPanel}>
                <div className={styles.diffPanelHeader}>Version A: {versionA}</div>
                <KoMonaco
                  language='json'
                  JSONdata={schemaA}
                  options={{ readOnly: true }}
                />
              </div>
              <div className={styles.diffPanel}>
                <div className={styles.diffPanelHeader}>Version B: {versionB}</div>
                <KoMonaco
                  language='json'
                  JSONdata={schemaB}
                  options={{ readOnly: true }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ContractDiff;
